import Box from '@mui/material/Box';
import { GridColDef, GridColumnVisibilityModel, GridSortItem } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAuth } from '@verantos-dev/core-auth';
import { VerantosOrgEnum } from '@verantos-dev/core-enums';
import { ActionMenu } from '@verantos-dev/shared-components';
import { clickableCellStyle } from '@verantos-dev/shared-styles';
import { formatDate } from '@verantos-dev/shared-utils';
import { ConceptGroup } from '@verantos-dev/types-concepts';

import {
  useConceptGroupsListDialogContext,
  ConceptGroupsDialogActionType,
  DialogType,
} from '../context/ConceptGroupsListDialogContext';

export const useConceptGroups = () => {
  const intl = useIntl();
  const { dispatch } = useConceptGroupsListDialogContext();
  const { user } = useAuth();

  const columns: GridColDef<ConceptGroup>[] = useMemo(
    () => [
      {
        field: 'conceptGroupId',
        headerName: intl.formatMessage({ id: 'conceptGroups.columns.omopConceptGroupId' }),
        flex: 1,
        minWidth: 300,
      },
      {
        field: 'conceptGroupName',
        headerName: intl.formatMessage({ id: 'conceptGroups.columns.conceptGroupName' }),
        flex: 2,
        minWidth: 300,
        renderCell: (params) => (
          <Box
            sx={clickableCellStyle}
            onClick={() =>
              dispatch({
                type: ConceptGroupsDialogActionType.OPEN_DIALOG,
                activeDialog: DialogType.DETAILS,
                data: { conceptGroupId: params.row.conceptGroupId },
              })
            }
            data-testid={`concept-group-name-${params.row.conceptGroupId}`}
          >
            {params.row.conceptGroupName}
          </Box>
        ),
      },
      {
        field: 'domains',
        headerName: intl.formatMessage({ id: 'conceptGroups.columns.domains' }),
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'conceptCount',
        headerName: intl.formatMessage({ id: 'conceptGroups.columns.conceptCount' }),
        flex: 1,
        minWidth: 150,
        type: 'number',
      },
      {
        field: 'owner',
        headerName: intl.formatMessage({ id: 'conceptGroups.columns.owner' }),
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'modifiedDate',
        headerName: intl.formatMessage({ id: 'conceptGroups.columns.modifiedDate' }),
        type: 'dateTime',
        minWidth: 230,
        flex: 0.5,
        valueGetter: (_, row) => {
          return row.modifiedBy?.transactionDate && new Date(row.modifiedBy.transactionDate);
        },
        renderCell: (params) => {
          const transactionDate = params.row.modifiedBy?.transactionDate
            ? formatDate(params.row.modifiedBy.transactionDate, 'dateTimeFull')
            : '--';
          return <Box>{transactionDate}</Box>;
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 80,
        minWidth: 80,
        maxWidth: 80,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        disableReorder: true,
        disableExport: true,
        renderCell: (params: any) => {
          // NOTE: If ownership model changes from user-based to org-based, revisit this check
          const isVerantosDefault =
            params.row.owner?.toLowerCase() === VerantosOrgEnum.VERANTOS.toLowerCase();

          if (isVerantosDefault) {
            return null;
          }

          // Allow delete for internal (Verantos) users or same-org users
          const canDelete =
            user.isInternalUser || params.row.organizationId === user.organizationId;

          if (!canDelete) {
            return null;
          }

          const actions = [
            {
              id: 'delete-concept-group',
              label: intl.formatMessage({ id: 'conceptGroups.actions.deleteConceptGroup' }),
              onClick: () =>
                dispatch({
                  type: ConceptGroupsDialogActionType.OPEN_DIALOG,
                  activeDialog: DialogType.DELETE,
                  data: { conceptGroupId: params.row.conceptGroupId },
                }),
              icon: 'delete',
              color: 'error',
            },
          ];

          return (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <ActionMenu
                id={`concept-group-actions-${params.row.conceptGroupId}`}
                menuItems={actions}
                data-testid={`concept-group-actions-${params.row.conceptGroupId}`}
                menuWidth={227}
              />
            </Box>
          );
        },
      },
    ],
    [intl, dispatch, user]
  );

  // Initial state sortModel for the data grid
  const sortModel: GridSortItem[] = [{ field: 'conceptGroupName', sort: 'asc' }];

  // Initial state columnVisibilityModel for the data grid
  const columnVisibilityModel: GridColumnVisibilityModel = {
    conceptGroupId: false,
    conceptGroupName: true,
    domains: true,
    conceptCount: true,
    owner: true,
    modifiedDate: true,
  };

  const getRowId = (row: ConceptGroup) => row.conceptGroupId;

  return {
    columns,
    getRowId,
    sortModel,
    columnVisibilityModel,
  };
};
